'use client';
import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, AreaChart, Area } from 'recharts';

const MONTHLY_SPEND = [
  { month: 'Feb', grn: 142000, gi: 98000 },
  { month: 'Mar', grn: 168000, gi: 112000 },
  { month: 'Apr', grn: 155000, gi: 104000 },
  { month: 'May', grn: 189000, gi: 131000 },
  { month: 'Jun', grn: 174000, gi: 118000 },
  { month: 'Jul', grn: 162000, gi: 94000 },
];

const SITES = [
  { name: 'Site Alpha', value: '€ 312,400', items: 284, users: 8, health: 94 },
  { name: 'Warehouse B', value: '€ 248,700', items: 196, users: 5, health: 88 },
  { name: 'Site Beta', value: '€ 187,200', items: 152, users: 6, health: 91 },
  { name: 'Site Gamma', value: '€ 99,020', items: 98, users: 4, health: 76 },
];

const USERS = [
  { name: 'Arjun Mehta', role: 'Store Manager', site: 'Site Alpha', lastLogin: '2h ago', status: 'active' },
  { name: 'Priya Sharma', role: 'QHSE Manager', site: 'All Sites', lastLogin: '4h ago', status: 'active' },
  { name: 'Rahul Kumar', role: 'Store Operator', site: 'Site Beta', lastLogin: '1d ago', status: 'active' },
  { name: 'Meena Pillai', role: 'Store Operator', site: 'Site Gamma', lastLogin: '3d ago', status: 'inactive' },
];

export default function CompanyAdminDashboard() {
  return (
    <div className="space-y-5">
      {/* Role header */}
      <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-violet-700 to-violet-900 rounded-2xl text-white">
        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
          <Icon name="BuildingOffice2Icon" size={22} />
        </div>
        <div>
          <p className="font-700 text-base">Business Overview</p>
          <p className="text-[12px] text-white/65">Company Admin · Larsen & Toubro — Construction · 4 sites</p>
        </div>
        <div className="ml-auto flex gap-2">
          <Link href="/settings/users" className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 rounded-lg text-[12px] font-600 transition-colors">
            <Icon name="UserPlusIcon" size={13} /> Add User
          </Link>
          <Link href="/reports" className="flex items-center gap-1.5 px-3 py-1.5 bg-accent text-accent-foreground hover:bg-amber-400 rounded-lg text-[12px] font-600 transition-colors">
            <Icon name="DocumentChartBarIcon" size={13} /> Reports
          </Link>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Inventory Value', value: '€ 847K', sub: '+€12,450 this week', icon: 'CubeIcon', color: 'bg-primary/5 border-primary/15', iconColor: 'bg-primary/10 text-primary' },
          { label: 'Monthly GRN Spend', value: '€ 162K', sub: 'July 2026 approved', icon: 'ArrowDownTrayIcon', color: 'bg-secondary/5 border-secondary/20', iconColor: 'bg-secondary/10 text-secondary' },
          { label: 'Active Users', value: '12', sub: '3 inactive this month', icon: 'UsersIcon', color: 'bg-violet-50 border-violet-200', iconColor: 'bg-violet-100 text-violet-600' },
          { label: 'Subscription', value: 'Pro', sub: 'Renews 01 Sep 2026', icon: 'CreditCardIcon', color: 'bg-teal/5 border-teal/20', iconColor: 'bg-teal/10 text-teal' },
        ].map((k) => (
          <div key={k.label} className={`rounded-xl border p-4 shadow-card ${k.color}`}>
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${k.iconColor}`}>
              <Icon name={k.icon as Parameters<typeof Icon>[0]['name']} size={18} />
            </div>
            <p className="text-[10px] font-600 uppercase tracking-widest text-muted-foreground">{k.label}</p>
            <p className="text-3xl font-700 text-foreground tabular-nums mt-0.5">{k.value}</p>
            <p className="text-[11px] text-muted-foreground mt-1">{k.sub}</p>
          </div>
        ))}
      </div>

      {/* Spend chart */}
      <div className="bg-card border border-border rounded-2xl shadow-card p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-600 text-foreground">Monthly Spend Trend</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">GRN receipts vs GI issuances (€)</p>
          </div>
          <div className="flex items-center gap-4 text-[11px]">
            <div className="flex items-center gap-1.5"><div className="w-3 h-1.5 rounded-full bg-secondary" /><span className="text-muted-foreground">GRN Receipts</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-1.5 rounded-full bg-teal" /><span className="text-muted-foreground">GI Issuances</span></div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart data={MONTHLY_SPEND}>
            <defs>
              <linearGradient id="grnGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#1E5FA0" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#1E5FA0" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="giGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#167A6E" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#167A6E" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E4E9F2" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: '#64748B' }} axisLine={false} tickLine={false} tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
            <Tooltip formatter={(v: number, name: string) => [`€${v.toLocaleString()}`, name === 'grn' ? 'GRN Receipts' : 'GI Issuances']} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #D1D9E6' }} />
            <Area type="monotone" dataKey="grn" stroke="#1E5FA0" strokeWidth={2} fill="url(#grnGrad)" />
            <Area type="monotone" dataKey="gi" stroke="#167A6E" strokeWidth={2} fill="url(#giGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Sites + Users */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-2xl shadow-card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Icon name="MapPinIcon" size={15} className="text-secondary" />
              <h3 className="text-sm font-600 text-foreground">Site Performance</h3>
            </div>
            <Link href="/settings/sites" className="text-[11px] text-secondary hover:underline">Manage sites</Link>
          </div>
          <div className="divide-y divide-border">
            {SITES.map((s) => (
              <div key={s.name} className="flex items-center gap-4 px-5 py-3.5 hover:bg-muted/30 transition-colors">
                <div className="w-8 h-8 rounded-lg bg-secondary/10 flex items-center justify-center shrink-0">
                  <Icon name="BuildingOfficeIcon" size={15} className="text-secondary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-600 text-foreground">{s.name}</p>
                  <p className="text-[10px] text-muted-foreground">{s.items} items · {s.users} users</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[12px] font-700 text-foreground tabular-nums">{s.value}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <div className="w-12 h-1 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${s.health >= 90 ? 'bg-teal' : s.health >= 80 ? 'bg-amber-400' : 'bg-red-500'}`} style={{ width: `${s.health}%` }} />
                    </div>
                    <span className="text-[10px] text-muted-foreground">{s.health}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl shadow-card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Icon name="UsersIcon" size={15} className="text-violet-600" />
              <h3 className="text-sm font-600 text-foreground">User Activity</h3>
            </div>
            <Link href="/settings/users" className="text-[11px] text-secondary hover:underline">All users</Link>
          </div>
          <div className="divide-y divide-border">
            {USERS.map((u) => (
              <div key={u.name} className="flex items-center gap-3 px-5 py-3.5 hover:bg-muted/30 transition-colors">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-[11px] font-700 shrink-0">
                  {u.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-600 text-foreground">{u.name}</p>
                  <p className="text-[10px] text-muted-foreground">{u.role} · {u.site}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] text-muted-foreground">{u.lastLogin}</p>
                  <span className={`text-[9px] px-2 py-0.5 rounded-full font-600 ${u.status === 'active' ? 'bg-teal/10 text-teal' : 'bg-muted text-muted-foreground'}`}>{u.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
